import { z } from "zod";
//#region src/api/orpc/util/cursorPagination.ts
/**
* Upper bound on `first`/`last`. Mirrors the artwork feed contract's `.max(50)`
* cap on its forward-only `first` arg — 100 is generous enough for the
* bi-directional cursor users (kaisuuken logs / search, contest, extra-pack
* boost, admin lists) without letting a caller request, say, 10k rows and
* pass that straight through to Prisma `take`.
*/
const PAGE_LIMIT_MAX = 100;
/**
* Bi-directional cursor pagination input. Mirrors the GraphQL Relay
* connection arguments — callers pass `first`/`after` for forward paging or
* `last`/`before` for backward paging.
*
* The mutual-exclusion rule (`first` XOR `last`) is enforced at runtime by
* the server's `parsePaginationInput` helper, and additionally at the
* contract boundary by `withCursorPaginationExclusivity` below. Routes that
* `.extend(...)` this base should chain that helper so the OpenAPI surface
* reflects the accepted inputs rather than the union of both fields.
*/
const cursorPaginationInputSchema = z.object({
	after: z.string().min(1).optional().describe("Cursor for forward pagination"),
	before: z.string().min(1).optional().describe("Cursor for backward pagination"),
	first: z.coerce.number().int().positive().max(PAGE_LIMIT_MAX).optional().describe("Number of items to fetch forward (used with after)"),
	last: z.coerce.number().int().positive().max(PAGE_LIMIT_MAX).optional().describe("Number of items to fetch backward (used with before)")
});
/**
* Wrap a cursor-pagination-shaped schema with the `first` XOR `last`
* exclusivity check. Kept as a chainable helper instead of baked into the
* base schema because `.refine(...)` returns a `ZodEffects`, which can't be
* `.extend(...)`ed — and most callers extend with their own filter fields.
*
* Apply at the route schema level: `cursorPaginationInputSchema.extend({...}).superRefine(...)`,
* or via this helper: `withCursorPaginationExclusivity(cursorPaginationInputSchema.extend({...}))`.
*/
function withCursorPaginationExclusivity(schema) {
	return schema.refine((input) => !(input.first != null && input.last != null), {
		message: "Cannot provide both first and last",
		path: ["first"]
	});
}
const cursorPageInfoSchema = z.object({
	startCursor: z.string().min(1).optional().describe("Cursor for the first item"),
	endCursor: z.string().min(1).optional().describe("Cursor for the last item"),
	hasNextPage: z.boolean().describe("Whether there are more pages"),
	hasPreviousPage: z.boolean().default(false).describe("Whether there is a previous page")
});
const createCursorListResponseSchema = (itemSchema) => z.object({
	data: z.array(itemSchema),
	pageInfo: cursorPageInfoSchema
});
//#endregion
export { createCursorListResponseSchema, cursorPageInfoSchema, cursorPaginationInputSchema, withCursorPaginationExclusivity };
